import { defineComponent as _defineComponent } from 'vue'
import window from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { MAX_NAME_LENGTH } from '@@/bits/numbers'
import { SnackbarNotificationType } from '@@/enums'
import OzAvatar from '@@/library/v4/components/OzAvatar.vue'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzInput, { OzInputSizePreset } from '@@/library/v4/components/OzInput.vue'
import OzLoadingSpinner from '@@/library/v4/components/OzLoadingSpinner.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceGuestStore } from '@@/pinia/surface_guest_store'
import { useSurfaceButtonSkinBasedTwClasses } from '@@/vuecomposables/surface_button_skin_based_tw_classes'
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'SurfaceGuestIdLabel',
  setup(__props) {

const AVATAR_SIZE = 24
const EXPAND_LABEL_TIMEOUT = 1000
const COLLAPSE_LABEL_WITHOUT_INTERACTING_TIMEOUT = 5000
const COLLAPSE_LABEL_AFTER_INTERACTING_TIMEOUT = 2000

const surfaceStore = useSurfaceStore()
const { buttonSkinBasedTailwindClasses } = useSurfaceButtonSkinBasedTwClasses()
const surfaceGuestStore = useSurfaceGuestStore()
const surfaceContainerSizeStore = useSurfaceContainerSizeStore()
const globalSnackbarStore = useGlobalSnackbarStore()

const isExpanded = ref<boolean>(false)
const isEditing = ref<boolean>(false)
const userNameInputRef = ref<InstanceType<typeof OzInput>>()
const isNameInputFocused = ref<boolean>(false)
const guestIdLabelRef = ref<InstanceType<typeof OzInput>>()

let labelTimeout

const isDoneButtonDisabled = computed<boolean>(() => {
  return (
    surfaceGuestStore.userName.trim() === '' ||
    !surfaceGuestStore.isUserNameValid ||
    surfaceGuestStore.isAwaitingServerResponse
  )
})

function stopLabelCollapseTimer() {
  if (!isExpanded.value) return

  clearTimeout(labelTimeout)
}

function startCollapseLabelTimer() {
  labelTimeout = setTimeout(() => {
    // Collapses the label after 2 seconds if the user doesn't interact with it, e.g. click or hover
    isExpanded.value = false
  }, COLLAPSE_LABEL_AFTER_INTERACTING_TIMEOUT)
}

function enterEditMode(): void {
  isEditing.value = true
  nextTick(() => {
    // Need to check it because the OzInput is placed inside the OzBaseButton,
    // and pressing the key space with trigger the click event of the OzBaseButton
    if (isNameInputFocused.value) return
    userNameInputRef.value?.focusInput()
    userNameInputRef.value?.selectInput()
  })
  stopLabelCollapseTimer()
}

async function saveName(): Promise<void> {
  if (isDoneButtonDisabled.value) return
  await surfaceGuestStore.updateSessionUserName()
  if (surfaceGuestStore.userNameErrorMessage !== '') {
    globalSnackbarStore.setSnackbar({
      message: surfaceGuestStore.userNameErrorMessage,
      notificationType: SnackbarNotificationType.error,
    })
  } else if (surfaceGuestStore.isUserNameValid) {
    startCollapseLabelTimer()
    isEditing.value = false
  }
}

function handleOnMouseLeaveOrBlur(): void {
  if (isEditing.value || !isExpanded.value) return
  // Don't collapse the label if the user is editing the name
  startCollapseLabelTimer()
}

function handleClickLabel(): void {
  if (isExpanded.value) {
    enterEditMode()
  } else {
    surfaceGuestStore.showGuestIdCard()
  }
}

function handleClickOutsideLabel(e: MouseEvent): void {
  if (!isExpanded.value || isEditing.value) return
  if (!guestIdLabelRef.value?.$el.contains(e.target as Node)) {
    isExpanded.value = false
  }
}

onMounted(() => {
  window.addEventListener('click', handleClickOutsideLabel)

  if (surfaceContainerSizeStore.isContainerSmallerThanTabletLandscape) {
    isExpanded.value = false
    return
  }

  surfaceGuestStore.setUserName(surfaceGuestStore.displayName)
  setTimeout(() => {
    // Expands the label 1 second after the user visits the page
    isExpanded.value = true
  }, EXPAND_LABEL_TIMEOUT)

  labelTimeout = setTimeout(() => {
    // Collapses the label after 5 seconds if the user hasn't interacted with it, e.g. click or hover
    isExpanded.value = false
  }, COLLAPSE_LABEL_WITHOUT_INTERACTING_TIMEOUT)
})

onBeforeUnmount(() => {
  window.removeEventListener('click', handleClickOutsideLabel)
})

return { __sfc: true,AVATAR_SIZE, EXPAND_LABEL_TIMEOUT, COLLAPSE_LABEL_WITHOUT_INTERACTING_TIMEOUT, COLLAPSE_LABEL_AFTER_INTERACTING_TIMEOUT, surfaceStore, buttonSkinBasedTailwindClasses, surfaceGuestStore, surfaceContainerSizeStore, globalSnackbarStore, isExpanded, isEditing, userNameInputRef, isNameInputFocused, guestIdLabelRef, labelTimeout, isDoneButtonDisabled, stopLabelCollapseTimer, startCollapseLabelTimer, enterEditMode, saveName, handleOnMouseLeaveOrBlur, handleClickLabel, handleClickOutsideLabel, __, MAX_NAME_LENGTH, OzAvatar, OzBaseButton, OzIcon, OzInput, OzInputSizePreset, OzLoadingSpinner, OzPlainButton, OzPlainButtonColorScheme, OzPlainButtonSizePreset }
}

})